import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Tooltip } from '@mui/material'

import { DocumentGenerationIcon } from '~assets/icons'
import {
  Avatar,
  Chip,
  Icon,
  IconButton,
  type SemanticIconName,
  Typography,
} from '~components/internal'
import { type AccentColorName, typography } from '~tokens/internal'
import { formatWrittenDateTime } from '~utils/dateUtils'
import { getFileTypeIconName, getFileTypeLabel, getFileViewUrl } from '~utils/fileUtils'

import './DocumentCard.scss'

export type DocumentCardCategory = 'submission' | 'generated' | 'other'

const categoryMeta: Record<DocumentCardCategory, { label: string; color: AccentColorName }> = {
  submission: { label: 'Submission', color: 'teal' },
  generated: { label: 'Generated', color: 'purple' },
  other: { label: 'Other', color: 'amber' },
}

export interface DocumentCardItem {
  key: string
  name: string
  created?: string
  url: string | null
  category: DocumentCardCategory
  addedBy?: { name: string | null; image: string | null }
  linkUrl?: string
}

function getFileTypeIcon(doc: DocumentCardItem): SemanticIconName | null {
  const iconName = getFileTypeIconName(doc.url ?? doc.name)
  return iconName === 'filePresent' && doc.category === 'generated' ? null : iconName
}

const DocumentCard: React.FC<{
  doc: DocumentCardItem
  trailing?: 'chip' | 'open'
  onClick?: () => void
}> = ({ doc, trailing = 'chip', onClick }) => {
  const navigate = useNavigate()
  const meta = categoryMeta[doc.category]
  const typeIcon = getFileTypeIcon(doc)
  const viewUrl = doc.url ? getFileViewUrl(doc.url) : null
  const isClickable = Boolean(onClick) || Boolean(doc.linkUrl) || Boolean(viewUrl)

  const handleClick = (): void => {
    if (onClick) {
      onClick()
    } else if (doc.linkUrl) {
      navigate(doc.linkUrl)
    } else if (viewUrl) {
      window.open(viewUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Box
      className={`document-card${isClickable ? ' document-card--clickable' : ''}`}
      {...(isClickable && {
        role: 'button',
        tabIndex: 0,
        onClick: handleClick,
        onKeyDown: (e: React.KeyboardEvent): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
          }
        },
      })}
    >
      <Avatar
        iconName={typeIcon ?? <DocumentGenerationIcon />}
        color={doc.category === 'generated' ? 'primary' : 'pink'}
        size={typography.fontSizePx.huge}
        variant='square'
      />
      <Box className='document-card__content'>
        <Box className='document-card__header'>
          <Typography variant='subtitle1'>{doc.name}</Typography>
        </Box>
        {doc.category !== 'generated' && doc.addedBy?.name ? (
          <Box className='document-card__meta-row'>
            <Box className='document-card__person'>
              <Typography variant='body2' color='textSecondary'>
                Added by
              </Typography>
              <Avatar
                src={doc.addedBy.image ?? undefined}
                alt={doc.addedBy.name}
                size={typography.fontSizePx.sm}
              >
                {doc.addedBy.name.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant='body2' color='textSecondary'>
                {doc.addedBy.name}
                {doc.created ? ` on ${formatWrittenDateTime(new Date(doc.created))}` : ''}
              </Typography>
            </Box>
          </Box>
        ) : !doc.created ? (
          <Box className='document-card__meta-row'>
            <Box className='document-card__date'>
              <Typography variant='body2' color='textSecondary'>
                {getFileTypeLabel(doc.url ?? doc.name)}
              </Typography>
            </Box>
          </Box>
        ) : doc.category === 'generated' ? (
          <Box className='document-card__meta-row'>
            <Box className='document-card__date'>
              <Typography variant='body2' color='textSecondary'>
                Generated on {formatWrittenDateTime(new Date(doc.created))}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box className='document-card__meta-row'>
            <Box className='document-card__person'>
              <Typography variant='body2' color='textSecondary'>
                {`Added on ${formatWrittenDateTime(new Date(doc.created))}`}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {trailing === 'chip' ? (
        <Chip label={meta.label} color={meta.color} size='small' />
      ) : (
        isClickable && (
          <Tooltip title='Open in new tab' placement='top'>
            <IconButton
              aria-label='Open in new tab'
              onClick={(event) => {
                event.stopPropagation()
                handleClick()
              }}
            >
              <Icon name='openInNew' size={typography.fontSizePx.md} />
            </IconButton>
          </Tooltip>
        )
      )}
    </Box>
  )
}

export default DocumentCard
